import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  Input,
  Select,
  Checkbox,
  RadioButton,
  TextArea,
  FieldTable,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";
import { getContact, getFacilityAddresses } from "../services/soar";

function KYALP() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [loadStatus, setLoadStatus] = useState({});
  const [addresses, setAddresses] = useState([]);

  const agencyOptions = useMemo(() => {
    return addresses.map((address) => {
      const facilityName = getFieldName(address.ccx_parentid);
      return { _id: facilityName, name: facilityName };
    });
  }, [addresses]);

  const agencyInfo = useMemo(() => {
    const obj = {};

    addresses.forEach((address) => {
      const facilityName = getFieldName(address.ccx_parentid);
      obj[facilityName] = {
        street: address.ccx_line1,
        city: address.ccx_city,
        state: address.ccx_state,
        postalCode: address.ccx_postalcode,
        telephone: address.ccx_telephone1,
      };
    });

    return obj;
  }, [addresses]);

  useEffect(() => {
    if (
      submissionid ||
      loadStatus.addressesLoaded ||
      loadStatus.addressesLoading
    ) {
      return;
    }

    const loadFacilityAddresses = async () => {
      setLoadStatus((ls) => {
        return { ...ls, addressesLoading: true };
      });
      const response = await getFacilityAddresses("SAFY of Kentucky");

      if (!isMountedRef.current || !response) {
        return;
      }

      setAddresses(response);
      setLoadStatus((ls) => {
        return { ...ls, addressesLoaded: true, addressesLoading: false };
      });
    };

    loadFacilityAddresses();
  }, [submissionid, loadStatus.addressesLoaded, loadStatus.addressesLoading]);

  useEffect(() => {
    if (
      submissionid ||
      !data.SelectedProgram ||
      loadStatus.clientInfoLoaded ||
      loadStatus.clientInfoLoading
    ) {
      return;
    }

    const loadClientInfo = async () => {
      setLoadStatus((ls) => {
        return { ...ls, clientInfoLoading: true };
      });
      const response = await getContact(clientid);

      if (!isMountedRef.current || !response?.fullName) {
        return;
      }

      const clientInfo = response[0];
      const obj = { ...data };
      obj.ClientName["@value"] = clientInfo.fullname;
      obj.PreferredName["@value"] =
        clientInfo.ccx_alias1 !== undefined
          ? clientInfo.ccx_alias2
            ? `${clientInfo.ccx_alias2}, ${clientInfo.ccx_alias1}`
            : `${clientInfo.lastname}, ${clientInfo.ccx_alias1}`
          : clientInfo.ccx_alias2
          ? `${clientInfo.ccx_alias2}, ${clientInfo.firstname}`
          : "";
      obj.DOB["@value"] = moment(getFieldDate(clientInfo.birthdate)).format(
        moment.HTML5_FMT.DATE
      );
      obj.SSNumber["@value"] = clientInfo.ccx_ssn;
      obj.Sex["@value"] = getFieldName(clientInfo.gendercode);
      obj.Gender["@value"] = getFieldName(clientInfo.sor_preferredgender);

      setData(obj);
      setLoadStatus((ls) => {
        return { ...ls, clientInfoLoaded: true, clientInfoLoading: false };
      });
    };

    loadClientInfo();
  }, [
    data,
    submissionid,
    loadStatus.clientInfoLoaded,
    loadStatus.clientInfoLoading,
    clientid,
  ]);

  const handleSelectedProgramChange = ({ currentTarget: input }) => {
    const obj = handleChange({ currentTarget: input });

    if (!clientData?.programs) {
      return;
    }

    const selectedProgram = clientData.programs.filter(
      (option) => option.ccx_client_programid === input.value
    )[0];

    if (!selectedProgram) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = selectedProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(selectedProgram.ccx_programunit);
    setFormsyDetails(details);

    obj.DateOfAdmission["@value"] = getFieldDate(selectedProgram.ccx_admitdate);
    setData(obj);
  };

  const handleAgencyChange = ({ currentTarget: input }) => {
    const obj = handleChange({ currentTarget: input });

    const agency = agencyInfo[input.value];
    obj.ProgramName[
      "@value"
    ] = `Specialized Alternatives for Families and Youth (SAFY)
${agency.street}
${agency.city}, ${agency.state} ${agency.postalCode}`;
    obj.Telephone["@value"] = agency.telephone;
    setData(obj);
  };

  const yNOptions = [
    { _id: "Y", name: "Y" },
    { _id: "N", name: "N" },
  ];

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const sexOptions = [
    { _id: "Male", name: "M" },
    { _id: "Female", name: "F" },
  ];

  const permanencyGoalOptions = [
    { _id: "Return To Parent", name: "Return To Parent" },
    { _id: "Adoption", name: "Adoption" },
    { _id: "Legal Guardianship", name: "Legal Guardianship" },
    {
      _id: "Permanent Relative Placement",
      name: "Permanent Relative Placement",
    },
    {
      _id: "Planned Permanent Living Arrangement",
      name: "Planned Permanent Living Arrangement",
    },
  ];

  ////UI
  return (
    <>
      <div className="clearfix">
        <div className="float-start mb-2">
          <BackIcon />
        </div>
        <div className="float-end mb-2">
          <a
            href="https://safy365.sharepoint.com/:w:/s/Kentucky/EXsclM3AiChLkMyNTjKoSYQBQBCo2jc_chxJ-OqQfwNVgg"
            target="_blank"
            rel="noreferrer"
          >
            Forms Information
          </a>
        </div>
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <p>
                Directions: Complete each section in full and submit the
                completed form to the Children's Review Program and the DCBS
                Worker. Failure to complete this form in full may result in a
                delayed level of care.
              </p>
              <h5>A. CHILD IDENTIFYING INFORMATION</h5>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col-3 py-2 text-center border-end">
                    Child's Name (Last, First)
                  </div>
                  <div className="col-2 py-2 text-center border-end">
                    Preferred Name
                  </div>
                  <div className="col-2 py-2 text-center border-end">
                    D.O.B.
                  </div>
                  <div className="col-2 py-2 text-center border-end">
                    Social Security #
                  </div>
                  <div className="col-3 py-2 text-center">
                    Date of Admission
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="ClientName"
                      error={errors.ClientName}
                      value={data.ClientName["@value"]}
                      onChange={handleChange}
                      identical={compare("ClientName")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="PreferredName"
                      error={errors.PreferredName}
                      value={data.PreferredName["@value"]}
                      onChange={handleChange}
                      identical={compare("PreferredName")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="DOB"
                      error={errors.DOB}
                      value={data.DOB["@value"]}
                      onChange={handleChange}
                      identical={compare("DOB")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="SSNumber"
                      error={errors.SSNumber}
                      value={data.SSNumber["@value"]}
                      onChange={handleChange}
                      identical={compare("SSNumber")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-3 py-2">
                    <Input
                      name="DateOfAdmission"
                      error={errors.DateOfAdmission}
                      value={data.DateOfAdmission["@value"]}
                      onChange={handleChange}
                      identical={compare("DateOfAdmission")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 text-center border-end">
                    Biological Sex
                  </div>
                  <div className="col-3 py-2 text-center border-end">
                    Gender Identity
                  </div>
                  <div className="col py-2 text-center border-end">
                    Sexual Orientation
                  </div>
                  <div className="col-2 py-2 text-center border-end">
                    Height
                  </div>
                  <div className="col-3 py-2 text-center">Weight</div>
                </div>
                <div className="row">
                  <div className="col-1 py-2 border-end">
                    <RadioButton
                      name="Sex"
                      error={errors.Sex}
                      value={data.Sex["@value"]}
                      options={sexOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsOnNewLine={true}
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="Gender"
                      error={errors.Gender}
                      value={data.Gender["@value"]}
                      onChange={handleChange}
                      identical={compare("Gender")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="SexualOrientation"
                      error={errors.SexualOrientation}
                      value={data.SexualOrientation["@value"]}
                      onChange={handleChange}
                      identical={compare("SexualOrientation")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="Height"
                      error={errors.Height}
                      value={data.Height["@value"]}
                      onChange={handleChange}
                      identical={compare("Height")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-3 py-2">
                    <Input
                      name="Weight"
                      error={errors.Weight}
                      value={data.Weight["@value"]}
                      onChange={handleChange}
                      identical={compare("Weight")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <br />
              <h5>B. SERVICE PROVIDER INFORMATION</h5>
              <div className="container border-start border-end border-top">
                <div className="row border-bottom">
                  <div className="col py-2 text-center border-end">Agency</div>
                  <div className="col py-2 text-center">
                    Program/Office Name
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2 border-end">
                    <Select
                      name="Agency"
                      error={errors.Agency}
                      value={data.Agency["@value"]}
                      options={agencyOptions}
                      onChange={handleAgencyChange}
                      identical={compare("Agency")}
                      commentsHandler={handleComments}
                      disabled={formDisabled || loadStatus.addressesLoading}
                    />
                  </div>
                  <div className="col py-2">
                    <TextArea
                      name="ProgramName"
                      error={errors.ProgramName}
                      value={data.ProgramName["@value"]}
                      onChange={handleChange}
                      identical={compare("ProgramName")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      rows="3"
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 py-2 text-center border-end">
                    Person Completing Form
                  </div>
                  <div className="col-2 py-2 text-center border-end">
                    Date Completed
                  </div>
                  <div className="col-3 py-2 text-center border-end">
                    Telephone
                  </div>
                  <div className="col-1 py-2 text-center border-end">Ext.</div>
                  <div className="col-2 py-2 text-center">Email</div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 py-2 border-end">
                    <Input
                      name="CompletedBy"
                      error={errors.CompletedBy}
                      value={data.CompletedBy["@value"]}
                      onChange={handleChange}
                      identical={compare("CompletedBy")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="DateCompleted"
                      error={errors.DateCompleted}
                      value={data.DateCompleted["@value"]}
                      onChange={handleChange}
                      identical={compare("DateCompleted")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="Telephone"
                      error={errors.Telephone}
                      value={data.Telephone["@value"]}
                      onChange={handleChange}
                      identical={compare("Telephone")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="Extension"
                      error={errors.Extension}
                      value={data.Extension["@value"]}
                      onChange={handleChange}
                      identical={compare("Extension")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-2 py-2">
                    <Input
                      name="Email"
                      error={errors.Email}
                      value={data.Email["@value"]}
                      onChange={handleChange}
                      identical={compare("Email")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2 text-center">
                    Service Dates Covered by Report
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2 text-center border-end">
                    Reporting Period Beginning
                  </div>
                  <div className="col py-2 text-center">
                    Reporting Period Ending
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2 text-center border-end">Month</div>
                  <div className="col py-2 text-center border-end">Day</div>
                  <div className="col py-2 text-center border-end">Year</div>
                  <div className="col py-2 text-center border-end">Month</div>
                  <div className="col py-2 text-center border-end">Day</div>
                  <div className="col py-2 text-center">Year</div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2 border-end">
                    <Input
                      name="StartMonth"
                      error={errors.StartMonth}
                      value={data.StartMonth["@value"]}
                      onChange={handleChange}
                      identical={compare("StartMonth")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="StartDay"
                      error={errors.StartDay}
                      value={data.StartDay["@value"]}
                      onChange={handleChange}
                      identical={compare("StartDay")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="StartYear"
                      error={errors.StartYear}
                      value={data.StartYear["@value"]}
                      onChange={handleChange}
                      identical={compare("StartYear")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="EndMonth"
                      error={errors.EndMonth}
                      value={data.EndMonth["@value"]}
                      onChange={handleChange}
                      identical={compare("EndMonth")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="EndDay"
                      error={errors.EndDay}
                      value={data.EndDay["@value"]}
                      onChange={handleChange}
                      identical={compare("EndDay")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="EndYear"
                      error={errors.EndYear}
                      value={data.EndYear["@value"]}
                      onChange={handleChange}
                      identical={compare("EndYear")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2 d-flex">
                    <b>FOSTER CARE ONLY</b>
                    <Checkbox
                      name="FosterCareNA"
                      label="NA"
                      checked={data.FosterCareNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col">
                    <Input
                      name="FosterFamily"
                      label="Current Foster Family (Include First &amp; Last Name)"
                      error={errors.FosterFamily}
                      value={data.FosterFamily["@value"]}
                      onChange={handleChange}
                      identical={compare("FosterFamily")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.FosterCareNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <div className="d-flex">
                      <span>
                        If there have been any changes in foster home placement
                        during this review period, including respite, specify
                        below.
                        <br />
                        Please provide a brief explanation for a placement move.
                      </span>
                      <Checkbox
                        name="PlacementChangesNA"
                        label="NA"
                        checked={data.PlacementChangesNA["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.FosterCareNA["@value"] === "true"
                        }
                        divClassName="ms-3"
                      />
                    </div>
                  </div>
                </div>
                <FieldTable
                  name="PlacementChangesDetails"
                  commentsHandler={handleComments}
                >
                  <div className="row border-bottom">
                    <div className="col py-2 text-center border-end">
                      Reason for Move
                    </div>
                    <div className="col py-2 text-center border-end">
                      From
                      <br />
                      (Name of Foster Family)
                    </div>
                    <div className="col py-2 text-center border-end">
                      To
                      <br />
                      (Name of Foster Family)
                    </div>
                    <div className="col py-2 text-center">Date of Move</div>
                  </div>
                  {data.PlacementChangesDetails["@value"] &&
                    JSON.parse(data.PlacementChangesDetails["@value"]).map(
                      (item, index) => (
                        <div key={item._id} className="row border-bottom">
                          <div className="col py-2 border-end d-flex">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <>
                                <i
                                  className="fas fa-save me-2 d-print-none"
                                  onClick={() => {
                                    handleSaveRow(
                                      index,
                                      "PlacementChangesDetails"
                                    );
                                  }}
                                ></i>
                                <i
                                  className="far fa-trash-alt me-2 d-print-none"
                                  onClick={() => {
                                    handleDeleteRow(
                                      index,
                                      "PlacementChangesDetails"
                                    );
                                  }}
                                ></i>
                                <Input
                                  name="Reason"
                                  value={item.Reason}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "PlacementChangesDetails"
                                    );
                                  }}
                                  disabled={
                                    formDisabled ||
                                    data.FosterCareNA["@value"] === "true" ||
                                    data.PlacementChangesNA["@value"] === "true"
                                  }
                                />
                              </>
                            ) : (
                              <>
                                {!formDisabled &&
                                data.FosterCareNA["@value"] !== "true" &&
                                data.PlacementChangesNA["@value"] !== "true" ? (
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "PlacementChangesDetails"
                                      );
                                    }}
                                  ></i>
                                ) : null}
                                {item.Reason}
                              </>
                            )}
                          </div>
                          <div className="col py-2 border-end">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="From"
                                value={item.From}
                                onChange={(e) => {
                                  handleRowChange(
                                    e,
                                    index,
                                    "PlacementChangesDetails"
                                  );
                                }}
                                disabled={
                                  formDisabled ||
                                  data.FosterCareNA["@value"] === "true" ||
                                  data.PlacementChangesNA["@value"] === "true"
                                }
                              />
                            ) : (
                              item.From
                            )}
                          </div>
                          <div className="col py-2 border-end">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="To"
                                value={item.To}
                                onChange={(e) => {
                                  handleRowChange(
                                    e,
                                    index,
                                    "PlacementChangesDetails"
                                  );
                                }}
                                disabled={
                                  formDisabled ||
                                  data.FosterCareNA["@value"] === "true" ||
                                  data.PlacementChangesNA["@value"] === "true"
                                }
                              />
                            ) : (
                              item.To
                            )}
                          </div>
                          <div className="col py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="Date"
                                value={item.Date}
                                onChange={(e) => {
                                  handleRowChange(
                                    e,
                                    index,
                                    "PlacementChangesDetails"
                                  );
                                }}
                                disabled={
                                  formDisabled ||
                                  data.FosterCareNA["@value"] === "true" ||
                                  data.PlacementChangesNA["@value"] === "true"
                                }
                              />
                            ) : (
                              item.Date
                            )}
                          </div>
                        </div>
                      )
                    )}
                  {errors.PlacementChangesDetails && (
                    <div className="alert alert-danger">
                      {errors.PlacementChangesDetails}
                    </div>
                  )}
                </FieldTable>
              </div>
              {data.PlacementChangesNA["@value"] !== "true" && !formDisabled ? (
                <>
                  <button
                    className="btn btn-primary ms-2 mt-2"
                    onClick={() => {
                      handleAddRow("PlacementChangesDetails", {
                        IsEditable: true,
                      });
                    }}
                  >
                    Add Foster Home Placement Change
                  </button>
                  <br />
                </>
              ) : null}
              <br />
              <h5>C. CHILD'S SSW INFORMATION</h5>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col-8 py-2 text-center border-end">
                    State Worker's (First &amp; Last Name){" "}
                  </div>
                  <div className="col-4 py-2 text-center">County</div>
                </div>
                <div className="row">
                  <div className="col-8 py-2 border-end">
                    <Input
                      name="StateWorker"
                      error={errors.StateWorker}
                      value={data.StateWorker["@value"]}
                      onChange={handleChange}
                      identical={compare("StateWorker")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-4 py-2">
                    <Input
                      name="County"
                      error={errors.County}
                      value={data.County["@value"]}
                      onChange={handleChange}
                      identical={compare("County")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <br />
              <h5>D. CHILD STRENGTHS/PROGRESS</h5>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="Strengths"
                      label="Identify this child's strengths/interests based upon the program's assessment."
                      error={errors.Strengths}
                      value={data.Strengths["@value"]}
                      onChange={handleChange}
                      identical={compare("Strengths")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    Please attach the following:
                    <br />
                    <br />
                    <Checkbox
                      name="TreatmentPlanAttached"
                      label="Current Treatment Plan or Document Summarizing Treatment Progress"
                      checked={data.TreatmentPlanAttached["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <Checkbox
                      name="CANSAttached"
                      label="CANS"
                      checked={data.CANSAttached["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <Checkbox
                      name="PSDAttached"
                      label="Comprehensive Assessment/Biopsychosocial Assessment (For Initial ALP completed by submitting program)"
                      checked={data.PSDAttached["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>E. RISK BEHAVIORS</h5>
                <Checkbox
                  name="RiskBehaviorsNA"
                  label="NA"
                  checked={data.RiskBehaviorsNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col">
                    Identify any significant behavioral issues and complete the
                    following information for each applicable behavior that has
                    occurred during this reporting period. Note if legal charges
                    were filed or medical attention was sought as a result of
                    the incident.{" "}
                    <b>
                      <u>
                        Do not report historical incidents (prior to this review
                        period) or use terms such as "ongoing" as a means to
                        communicate frequency.
                      </u>
                    </b>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 text-center border-end">NA</div>
                  <div className="col-3 py-2 text-center border-end">
                    Behaviors
                  </div>
                  <div className="col-2 py-2 text-center border-end">
                    Specific Dates of Occurence
                  </div>
                  <div className="col py-2 text-center">Details</div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="AnimalAbuseNA"
                      checked={data.AnimalAbuseNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Animal Maltreatment
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="AnimalAbuseDates"
                      error={errors.AnimalAbuseDates}
                      value={data.AnimalAbuseDates["@value"]}
                      onChange={handleChange}
                      identical={compare("AnimalAbuseDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.AnimalAbuseNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="AnimalAbuseDetails"
                      error={errors.AnimalAbuseDetails}
                      value={data.AnimalAbuseDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("AnimalAbuseDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.AnimalAbuseNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="BullyingNA"
                      checked={data.BullyingNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Bullying/Intimidating Others
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="BullyingDates"
                      error={errors.BullyingDates}
                      value={data.BullyingDates["@value"]}
                      onChange={handleChange}
                      identical={compare("BullyingDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.BullyingNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="BullyingDetails"
                      error={errors.BullyingDetails}
                      value={data.BullyingDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("BullyingDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.BullyingNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="DelinquentNA"
                      checked={data.DelinquentNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Delinquent / Criminal Behaviors / Conduct Related Issues
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="DelinquentDates"
                      error={errors.DelinquentDates}
                      value={data.DelinquentDates["@value"]}
                      onChange={handleChange}
                      identical={compare("DelinquentDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.DelinquentNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="DelinquentDetails"
                      error={errors.DelinquentDetails}
                      value={data.DelinquentDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("DelinquentDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.DelinquentNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="DestroyPropertyNA"
                      checked={data.DestroyPropertyNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Property Destruction / Vandalization
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="DestroyPropertyDates"
                      error={errors.DestroyPropertyDates}
                      value={data.DestroyPropertyDates["@value"]}
                      onChange={handleChange}
                      identical={compare("DestroyPropertyDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.DestroyPropertyNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="DestroyPropertyDetails"
                      error={errors.DestroyPropertyDetails}
                      value={data.DestroyPropertyDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("DestroyPropertyDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.DestroyPropertyNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="ArsonistNA"
                      checked={data.ArsonistNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">Fire Setting</div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="ArsonistDates"
                      error={errors.ArsonistDates}
                      value={data.ArsonistDates["@value"]}
                      onChange={handleChange}
                      identical={compare("ArsonistDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.ArsonistNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="ArsonistDetails"
                      error={errors.ArsonistDetails}
                      value={data.ArsonistDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("ArsonistDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.ArsonistNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="GangAffiliationNA"
                      checked={data.GangAffiliationNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Gang Affiliation / Interest
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="GangAffiliationDates"
                      error={errors.GangAffiliationDates}
                      value={data.GangAffiliationDates["@value"]}
                      onChange={handleChange}
                      identical={compare("GangAffiliationDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.GangAffiliationNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="GangAffiliationDetails"
                      error={errors.GangAffiliationDetails}
                      value={data.GangAffiliationDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("GangAffiliationDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.GangAffiliationNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="HomicidalNA"
                      checked={data.HomicidalNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Homicidal Threats / Plans
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="HomicidalDates"
                      error={errors.HomicidalDates}
                      value={data.HomicidalDates["@value"]}
                      onChange={handleChange}
                      identical={compare("HomicidalDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.HomicidalNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="HomicidalDetails"
                      error={errors.HomicidalDetails}
                      value={data.HomicidalDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("HomicidalDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.HomicidalNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="NonCompliantNA"
                      checked={data.NonCompliantNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Non-Compliant with Treatment Services
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="NonCompliantDates"
                      error={errors.NonCompliantDates}
                      value={data.NonCompliantDates["@value"]}
                      onChange={handleChange}
                      identical={compare("NonCompliantDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.NonCompliantNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="NonCompliantDetails"
                      error={errors.NonCompliantDetails}
                      value={data.NonCompliantDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("NonCompliantDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.NonCompliantNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="HarmOthersNA"
                      checked={data.HarmOthersNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Physical Aggression towards Others
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="HarmOthersDates"
                      error={errors.HarmOthersDates}
                      value={data.HarmOthersDates["@value"]}
                      onChange={handleChange}
                      identical={compare("HarmOthersDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.HarmOthersNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="HarmOthersDetails"
                      error={errors.HarmOthersDetails}
                      value={data.HarmOthersDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("HarmOthersDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.HarmOthersNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="RecklessNA"
                      checked={data.RecklessNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Reckless Behaviors
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="RecklessDates"
                      error={errors.RecklessDates}
                      value={data.RecklessDates["@value"]}
                      onChange={handleChange}
                      identical={compare("RecklessDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.RecklessNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="RecklessDetails"
                      error={errors.RecklessDetails}
                      value={data.RecklessDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("RecklessDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.RecklessNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="RunawayNA"
                      checked={data.RunawayNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Runaway (Only when child has left the premises)
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="RunawayDates"
                      error={errors.RunawayDates}
                      value={data.RunawayDates["@value"]}
                      onChange={handleChange}
                      identical={compare("RunawayDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.RunawayNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="RunawayDetails"
                      error={errors.RunawayDetails}
                      value={data.RunawayDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("RunawayDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.RunawayNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="SelfAbusiveNA"
                      checked={data.SelfAbusiveNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Self-Injurious Behaviors
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="SelfAbusiveDates"
                      error={errors.SelfAbusiveDates}
                      value={data.SelfAbusiveDates["@value"]}
                      onChange={handleChange}
                      identical={compare("SelfAbusiveDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.SelfAbusiveNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="SelfAbusiveDetails"
                      error={errors.SelfAbusiveDetails}
                      value={data.SelfAbusiveDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("SelfAbusiveDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.SelfAbusiveNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="SexualBehaviorsNA"
                      checked={data.SexualBehaviorsNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">Sexual Behaviors</div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="SexualBehaviorsDates"
                      error={errors.SexualBehaviorsDates}
                      value={data.SexualBehaviorsDates["@value"]}
                      onChange={handleChange}
                      identical={compare("SexualBehaviorsDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.SexualBehaviorsNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="SexualBehaviorsDetails"
                      error={errors.SexualBehaviorsDetails}
                      value={data.SexualBehaviorsDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("SexualBehaviorsDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.SexualBehaviorsNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="SubstanceAbuseNA"
                      checked={data.SubstanceAbuseNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Substance Misuse (Exclude tobacco use; include positive drug
                    screens.)
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="SubstanceAbuseDates"
                      error={errors.SubstanceAbuseDates}
                      value={data.SubstanceAbuseDates["@value"]}
                      onChange={handleChange}
                      identical={compare("SubstanceAbuseDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.SubstanceAbuseNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="SubstanceAbuseDetails"
                      error={errors.SubstanceAbuseDetails}
                      value={data.SubstanceAbuseDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("SubstanceAbuseDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.SubstanceAbuseNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="SuicidalNA"
                      checked={data.SuicidalNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Suicidal Behaviors (e.g. attempts, ideation, threats)
                  </div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="SuicidalDates"
                      error={errors.SuicidalDates}
                      value={data.SuicidalDates["@value"]}
                      onChange={handleChange}
                      identical={compare("SuicidalDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.SuicidalNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="SuicidalDetails"
                      error={errors.SuicidalDetails}
                      value={data.SuicidalDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("SuicidalDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.SuicidalNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="OtherBehaviorNA"
                      checked={data.OtherBehaviorNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">Other:</div>
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="OtherBehaviorDates"
                      error={errors.OtherBehaviorDates}
                      value={data.OtherBehaviorDates["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherBehaviorDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.OtherBehaviorNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="OtherBehaviorDetails"
                      error={errors.OtherBehaviorDetails}
                      value={data.OtherBehaviorDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherBehaviorDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.RiskBehaviorsNA["@value"] === "true" ||
                        data.OtherBehaviorNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>F. SOCIAL &amp; EMOTIONAL FUNCTIONING</h5>
                <Checkbox
                  name="SocialNA"
                  label="NA"
                  checked={data.SocialNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col">
                    Identify any significant social and emotional issues for
                    each area identified below that has occurred during this
                    reporting period.
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 text-center border-end">NA</div>
                  <div className="col-5 py-2 text-center border-end">Issue</div>
                  <div className="col-5 py-2 text-center">Details</div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="AngerControlNA"
                      checked={data.AngerControlNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">Anger Control</div>
                  <div className="col-6 py-2">
                    <Input
                      name="AngerControlDetails"
                      error={errors.AngerControlDetails}
                      value={data.AngerControlDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("AngerControlDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.AngerControlNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="AnxietyNA"
                      checked={data.AnxietyNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">Anxiety / Worry</div>
                  <div className="col-6 py-2">
                    <Input
                      name="AnxietyDetails"
                      error={errors.AnxietyDetails}
                      value={data.AnxietyDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("AnxietyDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.AnxietyNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="AttachmentNA"
                      checked={data.AttachmentNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">
                    Attachment Difficulties
                  </div>
                  <div className="col-6 py-2">
                    <Input
                      name="AttachmentDetails"
                      error={errors.AttachmentDetails}
                      value={data.AttachmentDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("AttachmentDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.AttachmentNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="AttentionNA"
                      checked={data.AttentionNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">
                    Attention / Impulsivity / Hyperactivity
                  </div>
                  <div className="col-6 py-2">
                    <Input
                      name="AttentionDetails"
                      error={errors.AttentionDetails}
                      value={data.AttentionDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("AttentionDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.AttentionNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="DepressionNA"
                      checked={data.DepressionNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">Depression</div>
                  <div className="col-6 py-2">
                    <Input
                      name="DepressionDetails"
                      error={errors.DepressionDetails}
                      value={data.DepressionDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("DepressionDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.DepressionNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="EatingDisturbancesNA"
                      checked={data.EatingDisturbancesNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">
                    Eating Disturbances
                  </div>
                  <div className="col-6 py-2">
                    <Input
                      name="EatingDisturbancesDetails"
                      error={errors.EatingDisturbancesDetails}
                      value={data.EatingDisturbancesDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("EatingDisturbancesDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.EatingDisturbancesNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="DefiantNA"
                      checked={data.DefiantNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">
                    Oppositional / Defiant (Non-compliance with authority)
                  </div>
                  <div className="col-6 py-2">
                    <Input
                      name="DefiantDetails"
                      error={errors.DefiantDetails}
                      value={data.DefiantDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("DefiantDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.DefiantNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="PsychosisNA"
                      checked={data.PsychosisNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">
                    Psychosis (Thought Disorder)
                  </div>
                  <div className="col-6 py-2">
                    <Input
                      name="PsychosisDetails"
                      error={errors.PsychosisDetails}
                      value={data.PsychosisDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychosisDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.PsychosisNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="SomatizationNA"
                      checked={data.SomatizationNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">Somatization</div>
                  <div className="col-6 py-2">
                    <Input
                      name="SomatizationDetails"
                      error={errors.SomatizationDetails}
                      value={data.SomatizationDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("SomatizationDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.SomatizationNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="TantrumsNA"
                      checked={data.TantrumsNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">Tantrums</div>
                  <div className="col-6 py-2">
                    <Input
                      name="TantrumsDetails"
                      error={errors.TantrumsDetails}
                      value={data.TantrumsDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("TantrumsDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.TantrumsNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="OtherSocialNA"
                      checked={data.OtherSocialNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.SocialNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-5 py-2 border-end">Other</div>
                  <div className="col-6 py-2">
                    <Input
                      name="OtherSocialDetails"
                      error={errors.OtherSocialDetails}
                      value={data.OtherSocialDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherSocialDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.SocialNA["@value"] === "true" ||
                        data.OtherSocialNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>
                  G. METHODS OF INTERVENTION USED DURING THIS REPORTING PERIODS
                </h5>
                <Checkbox
                  name="InterventionNA"
                  label="NA"
                  checked={data.InterventionNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    Complete the following for any services/interventions that
                    occurred during the reporting period that required the child
                    to stay in another location overnight (do not include home
                    visits, etc.)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 text-center border-end">NA</div>
                  <div className="col-3 py-2 text-center border-end">
                    Intervention
                  </div>
                  <div className="col py-2 text-center border-end">
                    Provider
                  </div>
                  <div className="col py-2 text-center border-end">Reason</div>
                  <div className="col py-2 text-center">Date(s)</div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="PsychiatricHospitalizationNA"
                      checked={
                        data.PsychiatricHospitalizationNA["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.InterventionNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Psychiatric Hospitalization
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="PsychiatricHospitalizationProvider"
                      error={errors.PsychiatricHospitalizationProvider}
                      value={data.PsychiatricHospitalizationProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychiatricHospitalizationProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.PsychiatricHospitalizationNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="PsychiatricHospitalizationReason"
                      error={errors.PsychiatricHospitalizationReason}
                      value={data.PsychiatricHospitalizationReason["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychiatricHospitalizationReason")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.PsychiatricHospitalizationNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="PsychiatricHospitalizationDates"
                      error={errors.PsychiatricHospitalizationDates}
                      value={data.PsychiatricHospitalizationDates["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychiatricHospitalizationDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.PsychiatricHospitalizationNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="CrisisStabilizationNA"
                      checked={data.CrisisStabilizationNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.InterventionNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Crisis Stabilization
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="CrisisStabilizationProvider"
                      error={errors.CrisisStabilizationProvider}
                      value={data.CrisisStabilizationProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("CrisisStabilizationProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.CrisisStabilizationNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="CrisisStabilizationReason"
                      error={errors.CrisisStabilizationReason}
                      value={data.CrisisStabilizationReason["@value"]}
                      onChange={handleChange}
                      identical={compare("CrisisStabilizationReason")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.CrisisStabilizationNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="CrisisStabilizationDates"
                      error={errors.CrisisStabilizationDates}
                      value={data.CrisisStabilizationDates["@value"]}
                      onChange={handleChange}
                      identical={compare("CrisisStabilizationDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.CrisisStabilizationNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="MedicalHospitalizationNA"
                      checked={
                        data.MedicalHospitalizationNA["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.InterventionNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Medical Hospitalization
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="MedicalHospitalizationProvider"
                      error={errors.MedicalHospitalizationProvider}
                      value={data.MedicalHospitalizationProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("MedicalHospitalizationProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.MedicalHospitalizationNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="MedicalHospitalizationReason"
                      error={errors.MedicalHospitalizationReason}
                      value={data.MedicalHospitalizationReason["@value"]}
                      onChange={handleChange}
                      identical={compare("MedicalHospitalizationReason")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.MedicalHospitalizationNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="MedicalHospitalizationDates"
                      error={errors.MedicalHospitalizationDates}
                      value={data.MedicalHospitalizationDates["@value"]}
                      onChange={handleChange}
                      identical={compare("MedicalHospitalizationDates")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.MedicalHospitalizationNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1"></div>
                  <div className="col-3 py-2 text-center border-end">
                    Intervention
                  </div>
                  <div className="col py-2 text-center border-end">
                    Provider
                  </div>
                  <div className="col py-2 text-center border-end">Reason</div>
                  <div className="col py-2 text-center">
                    Number of Times Utilized
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="TimeOutNA"
                      checked={data.TimeOutNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.InterventionNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Use of Time-out (Do not include self-time-out)
                  </div>
                  <div className="col py-2 border-end text-center">NA</div>
                  <div className="col py-2 border-end">
                    <Input
                      name="TimeOutReason"
                      error={errors.TimeOutReason}
                      value={data.TimeOutReason["@value"]}
                      onChange={handleChange}
                      identical={compare("TimeOutReason")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.TimeOutNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="TimeOutUtilized"
                      error={errors.TimeOutUtilized}
                      value={data.TimeOutUtilized["@value"]}
                      onChange={handleChange}
                      identical={compare("TimeOutUtilized")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.TimeOutNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="PhysicalManagementNA"
                      checked={data.PhysicalManagementNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.InterventionNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Physical Management including assists
                  </div>
                  <div className="col py-2 border-end text-center">NA</div>
                  <div className="col py-2 border-end">
                    <Input
                      name="PhysicalManagementReason"
                      error={errors.PhysicalManagementReason}
                      value={data.PhysicalManagementReason["@value"]}
                      onChange={handleChange}
                      identical={compare("PhysicalManagementReason")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.PhysicalManagementNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="PhysicalManagementUtilized"
                      error={errors.PhysicalManagementUtilized}
                      value={data.PhysicalManagementUtilized["@value"]}
                      onChange={handleChange}
                      identical={compare("PhysicalManagementUtilized")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.PhysicalManagementNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="OtherPhysicalInterventionNA"
                      checked={
                        data.OtherPhysicalInterventionNA["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.InterventionNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Other Physical Interventions (escorts, etc.)
                  </div>
                  <div className="col py-2 border-end text-center">NA</div>
                  <div className="col py-2 border-end">
                    <Input
                      name="OtherPhysicalInterventionReason"
                      error={errors.OtherPhysicalInterventionReason}
                      value={data.OtherPhysicalInterventionReason["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherPhysicalInterventionReason")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.OtherPhysicalInterventionNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="OtherPhysicalInterventionUtilized"
                      error={errors.OtherPhysicalInterventionUtilized}
                      value={data.OtherPhysicalInterventionUtilized["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherPhysicalInterventionUtilized")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.OtherPhysicalInterventionNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="AssistanceNA"
                      checked={data.AssistanceNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.InterventionNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Calling outside assistance (e.g. police, on-call agency
                    staff)
                  </div>
                  <div className="col py-2 border-end text-center">NA</div>
                  <div className="col py-2 border-end">
                    <Input
                      name="AssistanceReason"
                      error={errors.AssistanceReason}
                      value={data.AssistanceReason["@value"]}
                      onChange={handleChange}
                      identical={compare("AssistanceReason")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.AssistanceNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="AssistanceUtilized"
                      error={errors.AssistanceUtilized}
                      value={data.AssistanceUtilized["@value"]}
                      onChange={handleChange}
                      identical={compare("AssistanceUtilized")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.AssistanceNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="InvoluntaryConfinementNA"
                      checked={
                        data.InvoluntaryConfinementNA["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.InterventionNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    Involuntary Confinement
                  </div>
                  <div className="col py-2 border-end text-center">NA</div>
                  <div className="col py-2 border-end">
                    <Input
                      name="InvoluntaryConfinementReason"
                      error={errors.InvoluntaryConfinementReason}
                      value={data.InvoluntaryConfinementReason["@value"]}
                      onChange={handleChange}
                      identical={compare("InvoluntaryConfinementReason")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.InvoluntaryConfinementNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="InvoluntaryConfinementUtilized"
                      error={errors.InvoluntaryConfinementUtilized}
                      value={data.InvoluntaryConfinementUtilized["@value"]}
                      onChange={handleChange}
                      identical={compare("InvoluntaryConfinementUtilized")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.InvoluntaryConfinementNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="OtherInterventionNA"
                      checked={data.OtherInterventionNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.InterventionNA["@value"] === "true"
                      }
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="OtherInterventionDetails"
                      label="Other (explain):"
                      error={errors.OtherInterventionDetails}
                      value={data.OtherInterventionDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherInterventionDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.OtherInterventionNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="OtherInterventionProvider"
                      error={errors.OtherInterventionProvider}
                      value={data.OtherInterventionProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherInterventionProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.OtherInterventionNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="OtherInterventionReason"
                      error={errors.OtherInterventionReason}
                      value={data.OtherInterventionReason["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherInterventionReason")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.OtherInterventionNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    <Input
                      name="OtherInterventionUtilized"
                      error={errors.OtherInterventionUtilized}
                      value={data.OtherInterventionUtilized["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherInterventionUtilized")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.InterventionNA["@value"] === "true" ||
                        data.OtherInterventionNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
              </div>
              <br />
              <h5>H. SUPERVISION</h5>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    <TextArea
                      name="SupervisionDetails"
                      label="Describe the supervision provided for this child, including caregiver to child ratio, oversight, etc."
                      error={errors.SupervisionDetails}
                      value={data.SupervisionDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("SupervisionDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <TextArea
                      name="SupervisionPlan"
                      label="Identify or attach the supervision plan this child currently has in place to ensure the safety of the child and others."
                      error={errors.SupervisionPlan}
                      value={data.SupervisionPlan["@value"]}
                      onChange={handleChange}
                      identical={compare("SupervisionPlan")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>I. MEDICAL ISSUES</h5>
                <Checkbox
                  name="MedicalNA"
                  label="NA"
                  checked={data.MedicalNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border">
                <div className="row">
                  <div className="col py-2">
                    <TextArea
                      name="MedicalTreatment"
                      label="Describe any significant medical issues (including medically complex diagnoses) for which the child has received treatment during this reporting period and explain each condition and how caregiver time and resources were utilized."
                      error={errors.MedicalTreatment}
                      value={data.MedicalTreatment["@value"]}
                      onChange={handleChange}
                      identical={compare("MedicalTreatment")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.MedicalNA["@value"] === "true"
                      }
                      rows="3"
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>J. MEDICATIONS</h5>
                <Checkbox
                  name="MedicationsNA"
                  label="NA"
                  checked={data.MedicationsNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border-top border-start border-end">
                <div className="row border-bottom">
                  <div className="col py-2">
                    List the child's current medications:
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">#</div>
                  <div className="col py-2 border-end">Medication</div>
                  <div className="col py-2 border-end">Purpose</div>
                  <div className="col-1 py-2 border-end">#</div>
                  <div className="col py-2 border-end">Medication</div>
                  <div className="col py-2">Purpose</div>
                </div>
                <FieldTable name="Medications" commentsHandler={handleComments}>
                  {data.Medications["@value"] &&
                    JSON.parse(data.Medications["@value"]).map(
                      (item, index) => {
                        if (index % 2 === 0) {
                          const meds = JSON.parse(data.Medications["@value"]);
                          let next;
                          if (meds.length > index + 1) next = meds[index + 1];

                          return (
                            <React.Fragment key={item._id}>
                              <div className="row border-bottom">
                                <div className="col-1 py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      {index + 1}
                                      <br />
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(index, "Medications");
                                        }}
                                      ></i>
                                      <br />
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(index, "Medications");
                                        }}
                                      ></i>
                                    </>
                                  ) : (
                                    <>
                                      {index + 1}
                                      <br />
                                      {!formDisabled &&
                                      data.MedicationsNA["@value"] !==
                                        "true" ? (
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(index, "Medications");
                                          }}
                                        ></i>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                                <div className="col py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Medication"
                                      value={item.Medication}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "Medications"
                                        );
                                      }}
                                      disabled={
                                        formDisabled ||
                                        data.MedicationsNA["@value"] === "true"
                                      }
                                    />
                                  ) : (
                                    item.Medication
                                  )}
                                </div>
                                <div className="col py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Purpose"
                                      value={item.Purpose}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "Medications"
                                        );
                                      }}
                                      disabled={
                                        formDisabled ||
                                        data.MedicationsNA["@value"] === "true"
                                      }
                                    />
                                  ) : (
                                    item.Purpose
                                  )}
                                </div>
                                <div className="col-1 py-2 border-end">
                                  {next !== undefined ? (
                                    <>
                                      {next.IsEditable === undefined ||
                                      next.IsEditable ? (
                                        <>
                                          {index + 2}
                                          <br />
                                          <i
                                            className="fas fa-save me-2 d-print-none"
                                            onClick={() => {
                                              handleSaveRow(
                                                index,
                                                "Medications"
                                              );
                                            }}
                                          ></i>
                                          <br />
                                          <i
                                            className="far fa-trash-alt me-2 d-print-none"
                                            onClick={() => {
                                              handleDeleteRow(
                                                index,
                                                "Medications"
                                              );
                                            }}
                                          ></i>
                                        </>
                                      ) : (
                                        <>
                                          {index + 2}
                                          <br />
                                          {!formDisabled &&
                                          data.MedicationsNA["@value"] !==
                                            "true" ? (
                                            <i
                                              className="fas fa-pen me-2 d-print-none"
                                              onClick={() => {
                                                handleEditRow(
                                                  index,
                                                  "Medications"
                                                );
                                              }}
                                            ></i>
                                          ) : null}
                                        </>
                                      )}
                                    </>
                                  ) : null}
                                </div>
                                <div className="col py-2 border-end">
                                  {next !== undefined ? (
                                    <>
                                      {next.IsEditable === undefined ||
                                      next.IsEditable ? (
                                        <Input
                                          name="Medication"
                                          value={next.Medication}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "Medications"
                                            );
                                          }}
                                          disabled={
                                            formDisabled ||
                                            data.MedicationsNA["@value"] ===
                                              "true"
                                          }
                                        />
                                      ) : (
                                        next.Medication
                                      )}
                                    </>
                                  ) : null}
                                </div>
                                <div className="col py-2">
                                  {next !== undefined ? (
                                    <>
                                      {next.IsEditable === undefined ||
                                      next.IsEditable ? (
                                        <Input
                                          name="Purpose"
                                          value={next.Purpose}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "Medications"
                                            );
                                          }}
                                          disabled={
                                            formDisabled ||
                                            data.MedicationsNA["@value"] ===
                                              "true"
                                          }
                                        />
                                      ) : (
                                        next.Purpose
                                      )}
                                    </>
                                  ) : null}
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        } else return null;
                      }
                    )}
                  {errors.Medications && (
                    <div className="alert alert-danger">
                      {errors.Medications}
                    </div>
                  )}
                </FieldTable>
              </div>
              {!formDisabled && data.MedicationsNA["@value"] !== "true" ? (
                <>
                  <button
                    className="btn btn-primary ms-2 mt-2"
                    onClick={() => {
                      handleAddFieldTableRow(data, setData, "Medications", {
                        IsEditable: true,
                      });
                    }}
                  >
                    Add Medication
                  </button>
                  <br />
                </>
              ) : null}
              <br />
              <div className="d-flex">
                <h5>K. MENTAL HEALTH DIAGNOSES AND EVALUATIONS</h5>
                <Checkbox
                  name="MentalHealthNA"
                  label="NA"
                  checked={data.MentalHealthNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="CurrentDiagnoses"
                      label="List the child's current diagnoses based on the latest edition of the Diagnostic and Statistical Manual (DSM) of the American Psychiatric Association. (Do not use codes only for the diagnosis)"
                      error={errors.CurrentDiagnoses}
                      value={data.CurrentDiagnoses["@value"]}
                      onChange={handleChange}
                      identical={compare("CurrentDiagnoses")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.MentalHealthNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <RadioButton
                      name="ChangeInDiagnosis"
                      label="Is the current diagnosis different from what was listed on the previous ALP?"
                      error={errors.ChangeInDiagnosis}
                      value={data.ChangeInDiagnosis["@value"]}
                      options={yNOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.MentalHealthNA["@value"] === "true"
                      }
                    />
                    {data.ChangeInDiagnosis["@value"] !== "No" ? (
                      <Input
                        name="ReasonForChange"
                        label="If Yes, explain the reason for the change in diagnosis."
                        error={errors.ReasonForChange}
                        value={data.ReasonForChange["@value"]}
                        onChange={handleChange}
                        identical={compare("ReasonForChange")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.MentalHealthNA["@value"] === "true"
                        }
                      />
                    ) : null}
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    If the child has had a psychological evaluation during this
                    reporting period, please submit it with this ALP. If this is
                    the first time your agency is including a diagnosis of an
                    intellectual or developmental disability for this child,
                    include documentation supporting this diagnosis (e.g. IQ
                    testing, IEP).
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>L. TRAUMA RELATED ISSUES</h5>
                <Checkbox
                  name="TraumaNA"
                  label="NA"
                  checked={data.TraumaNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border-start border-end border-top">
                <div className="row">
                  <div className="col py-2">
                    Identify any issues related to past or current trauma that
                    have been identified and are currently being addressed by
                    your program. If the child has experienced or has revealed
                    any new trauma during this reporting period, please provide
                    details and how your program is addressing trauma in
                    treatment.
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <RadioButton
                      name="TraumaReported"
                      label="Issue has been reported to DCBS?"
                      error={errors.TraumaReported}
                      value={data.TraumaReported["@value"]}
                      options={yNOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.TraumaNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2">
                    {data.TraumaReported["@value"] !== "No" ? (
                      <Input
                        name="TraumaReportDate"
                        label="Date Reported:"
                        error={errors.TraumaReportDate}
                        value={data.TraumaReportDate["@value"]}
                        onChange={handleChange}
                        identical={compare("TraumaReportDate")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.TraumaNA["@value"] === "true"
                        }
                      />
                    ) : null}
                  </div>
                </div>
                <FieldTable
                  name="TraumaIssues"
                  commentsHandler={handleComments}
                >
                  <div className="row border-bottom">
                    <div className="col-3 border-end py-2">Issue</div>
                    <div className="col py-2">
                      Please explain how the child's trauma is currently being
                      addressed in treatment. (Not necessary if an attached
                      treatment plan addresses this.)
                    </div>
                  </div>
                  {data.TraumaIssues["@value"] &&
                    JSON.parse(data.TraumaIssues["@value"]).map(
                      (item, index) => (
                        <div key={item._id} className="row border-bottom">
                          <div className="col-3 border-end py-2 d-flex">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <>
                                <i
                                  className="fas fa-save me-2 d-print-none"
                                  onClick={() => {
                                    handleSaveRow(index, "TraumaIssues");
                                  }}
                                ></i>
                                <i
                                  className="far fa-trash-alt me-2 d-print-none"
                                  onClick={() => {
                                    handleDeleteRow(index, "TraumaIssues");
                                  }}
                                ></i>
                                <Input
                                  name="Issue"
                                  value={item.Issue}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "TraumaIssues");
                                  }}
                                  disabled={
                                    formDisabled ||
                                    data.TraumaNA["@value"] === "true"
                                  }
                                />
                              </>
                            ) : (
                              <>
                                {!formDisabled &&
                                data.TraumaNA["@value"] !== "true" ? (
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "TraumaIssues");
                                    }}
                                  ></i>
                                ) : null}
                                {item.Issue}
                              </>
                            )}
                          </div>
                          <div className="col py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="Plan"
                                value={item.Plan}
                                onChange={(e) => {
                                  handleRowChange(e, index, "TraumaIssues");
                                }}
                                disabled={
                                  formDisabled ||
                                  data.TraumaNA["@value"] === "true"
                                }
                              />
                            ) : (
                              item.Plan
                            )}
                          </div>
                        </div>
                      )
                    )}
                  {errors.TraumaIssues && (
                    <div className="alert alert-danger">
                      {errors.TraumaIssues}
                    </div>
                  )}
                </FieldTable>
              </div>
              {!formDisabled && data.TraumaNA["@value"] !== "true" ? (
                <>
                  <button
                    className="btn btn-primary ms-2 mt-2"
                    onClick={() => {
                      handleAddFieldTableRow(data, setData, "TraumaIssues", {
                        IsEditable: true,
                      });
                    }}
                  >
                    Add Issue
                  </button>
                </>
              ) : null}
              <br />
              <br />
              <div className="d-flex">
                <span className="me-2">
                  <h5>M. SERVICES PROVIDED</h5>
                </span>
                <Checkbox
                  name="ServicesProvidedNA"
                  label="NA"
                  checked={data.ServicesProvidedNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                <span className="ms-3">
                  (
                  <Link
                    to={`/client/${clientid}?tab=documentation`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-print-none"
                  >
                    Documentation
                  </Link>
                  )
                </span>
              </div>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    Provide details of any mental health services this child has
                    received during this reporting period.
                  </div>
                </div>
                <div className="row border-bottom">
                  <div
                    className="col-3 py-2 text-center border-end"
                    title="Note that each session should only be counted for one service. For example, the same session cannot be counted as both a case management and an individual therapy session."
                  >
                    Service <i className="fa fa-info-circle text-primary"></i>
                  </div>
                  <div className="col-1 py-2 text-center border-end">
                    # Sessions
                  </div>
                  <div className="col py-2 text-center border-end">
                    Name of Agency / Program Providing Service
                  </div>
                  <div className="col-3 py-2 text-center border-end">
                    Provider Name(s)
                  </div>
                  <div className="col py-2 text-center border-end">
                    Identify Degree &amp; License, if applicable
                  </div>
                  <div className="col-1 py-2 text-center">
                    QMHP 5S Programs Only
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">Case Management</div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="CaseManagementSessions"
                      error={errors.CaseManagementSessions}
                      value={data.CaseManagementSessions["@value"]}
                      onChange={handleChange}
                      identical={compare("CaseManagementSessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="CaseManagementAgency"
                      error={errors.CaseManagementAgency}
                      value={data.CaseManagementAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("CaseManagementAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="CaseManagementProvider"
                      error={errors.CaseManagementProvider}
                      value={data.CaseManagementProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("CaseManagementProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="CaseManagementCredentials"
                      error={errors.CaseManagementCredentials}
                      value={data.CaseManagementCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("CaseManagementCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2"></div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">
                    Individual Therapy
                    <br />
                    <div className="d-flex">
                      <span>Contracted Provider</span>
                      <RadioButton
                        name="IndividualTherapyContract"
                        error={errors.IndividualTherapyContract}
                        value={data.IndividualTherapyContract["@value"]}
                        options={yNOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.ServicesProvidedNA["@value"] === "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="IndividualTherapySessions"
                      error={errors.IndividualTherapySessions}
                      value={data.IndividualTherapySessions["@value"]}
                      onChange={handleChange}
                      identical={compare("IndividualTherapySessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="IndividualTherapyAgency"
                      error={errors.IndividualTherapyAgency}
                      value={data.IndividualTherapyAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("IndividualTherapyAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="IndividualTherapyProvider"
                      error={errors.IndividualTherapyProvider}
                      value={data.IndividualTherapyProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("IndividualTherapyProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="IndividualTherapyCredentials"
                      error={errors.IndividualTherapyCredentials}
                      value={data.IndividualTherapyCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("IndividualTherapyCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2">
                    <RadioButton
                      name="IndividualTherapyQMHPOnly"
                      error={errors.IndividualTherapyQMHPOnly}
                      value={data.IndividualTherapyQMHPOnly["@value"]}
                      options={yNOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">
                    Family Therapy
                    <br />
                    <Input
                      name="FamilyTherapyMembers"
                      label="Identify participating members based on their relationship to the child (e.g., adoptive mother and stepfather, biological father, siblings, aunt, foster parents):"
                      error={errors.FamilyTherapyMembers}
                      value={data.FamilyTherapyMembers["@value"]}
                      onChange={handleChange}
                      identical={compare("FamilyTherapyMembers")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="FamilyTherapySessions"
                      error={errors.FamilyTherapySessions}
                      value={data.FamilyTherapySessions["@value"]}
                      onChange={handleChange}
                      identical={compare("FamilyTherapySessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="FamilyTherapyAgency"
                      error={errors.FamilyTherapyAgency}
                      value={data.FamilyTherapyAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("FamilyTherapyAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="FamilyTherapyProvider"
                      error={errors.FamilyTherapyProvider}
                      value={data.FamilyTherapyProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("FamilyTherapyProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="FamilyTherapyCredentials"
                      error={errors.FamilyTherapyCredentials}
                      value={data.FamilyTherapyCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("FamilyTherapyCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2">
                    <RadioButton
                      name="FamilyTherapyQMHPOnly"
                      error={errors.FamilyTherapyQMHPOnly}
                      value={data.FamilyTherapyQMHPOnly["@value"]}
                      options={yNOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">Group Therapy </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="GroupTherapySessions"
                      error={errors.GroupTherapySessions}
                      value={data.GroupTherapySessions["@value"]}
                      onChange={handleChange}
                      identical={compare("GroupTherapySessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="GroupTherapyAgency"
                      error={errors.GroupTherapyAgency}
                      value={data.GroupTherapyAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("GroupTherapyAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="GroupTherapyProvider"
                      error={errors.GroupTherapyProvider}
                      value={data.GroupTherapyProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("GroupTherapyProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="GroupTherapyCredentials"
                      error={errors.GroupTherapyCredentials}
                      value={data.GroupTherapyCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("GroupTherapyCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2">
                    <RadioButton
                      name="GroupTherapyQMHPOnly"
                      error={errors.GroupTherapyQMHPOnly}
                      value={data.GroupTherapyQMHPOnly["@value"]}
                      options={yNOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">
                    Psychoeducation Group Therapy
                  </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="PsychoeducationTherapySessions"
                      error={errors.PsychoeducationTherapySessions}
                      value={data.PsychoeducationTherapySessions["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychoeducationTherapySessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="PsychoeducationTherapyAgency"
                      error={errors.PsychoeducationTherapyAgency}
                      value={data.PsychoeducationTherapyAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychoeducationTherapyAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="PsychoeducationTherapyProvider"
                      error={errors.PsychoeducationTherapyProvider}
                      value={data.PsychoeducationTherapyProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychoeducationTherapyProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="PsychoeducationTherapyCredentials"
                      error={errors.PsychoeducationTherapyCredentials}
                      value={data.PsychoeducationTherapyCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychoeducationTherapyCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2"></div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">
                    Psychiatric (medication management)
                  </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="PsychiatricSessions"
                      error={errors.PsychiatricSessions}
                      value={data.PsychiatricSessions["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychiatricSessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="PsychiatricAgency"
                      error={errors.PsychiatricAgency}
                      value={data.PsychiatricAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychiatricAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="PsychiatricProvider"
                      error={errors.PsychiatricProvider}
                      value={data.PsychiatricProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychiatricProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="PsychiatricCredentials"
                      error={errors.PsychiatricCredentials}
                      value={data.PsychiatricCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychiatricCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2"></div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">Substance Abuse</div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="SubstanceAbuseSessions"
                      error={errors.SubstanceAbuseSessions}
                      value={data.SubstanceAbuseSessions["@value"]}
                      onChange={handleChange}
                      identical={compare("SubstanceAbuseSessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="SubstanceAbuseAgency"
                      error={errors.SubstanceAbuseAgency}
                      value={data.SubstanceAbuseAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("SubstanceAbuseAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="SubstanceAbuseProvider"
                      error={errors.SubstanceAbuseProvider}
                      value={data.SubstanceAbuseProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("SubstanceAbuseProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="SubstanceAbuseCredentials"
                      error={errors.SubstanceAbuseCredentials}
                      value={data.SubstanceAbuseCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("SubstanceAbuseCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2"></div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">
                    Exploitation Services
                  </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="ExploitationSessions"
                      error={errors.ExploitationSessions}
                      value={data.ExploitationSessions["@value"]}
                      onChange={handleChange}
                      identical={compare("ExploitationSessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="ExploitationAgency"
                      error={errors.ExploitationAgency}
                      value={data.ExploitationAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("ExploitationAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="ExploitationProvider"
                      error={errors.ExploitationProvider}
                      value={data.ExploitationProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("ExploitationProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="ExploitationCredentials"
                      error={errors.ExploitationCredentials}
                      value={data.ExploitationCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("ExploitationCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2"></div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">
                    Sexual Offender Treatment
                  </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="SexualOffenderSessions"
                      error={errors.SexualOffenderSessions}
                      value={data.SexualOffenderSessions["@value"]}
                      onChange={handleChange}
                      identical={compare("SexualOffenderSessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="SexualOffenderAgency"
                      error={errors.SexualOffenderAgency}
                      value={data.SexualOffenderAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("SexualOffenderAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="SexualOffenderProvider"
                      error={errors.SexualOffenderProvider}
                      value={data.SexualOffenderProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("SexualOffenderProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="SexualOffenderCredentials"
                      error={errors.SexualOffenderCredentials}
                      value={data.SexualOffenderCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("SexualOffenderCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2"></div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">
                    Sexually Reactive Treatment
                  </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="SexuallyReactiveSessions"
                      error={errors.SexuallyReactiveSessions}
                      value={data.SexuallyReactiveSessions["@value"]}
                      onChange={handleChange}
                      identical={compare("SexuallyReactiveSessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="SexuallyReactiveAgency"
                      error={errors.SexuallyReactiveAgency}
                      value={data.SexuallyReactiveAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("SexuallyReactiveAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="SexuallyReactiveProvider"
                      error={errors.SexuallyReactiveProvider}
                      value={data.SexuallyReactiveProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("SexuallyReactiveProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="SexuallyReactiveCredentials"
                      error={errors.SexuallyReactiveCredentials}
                      value={data.SexuallyReactiveCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("SexuallyReactiveCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2"></div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">
                    Independent Living (Ages 14+)
                  </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="IndependentLivingSessions"
                      error={errors.IndependentLivingSessions}
                      value={data.IndependentLivingSessions["@value"]}
                      onChange={handleChange}
                      identical={compare("IndependentLivingSessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="IndependentLivingAgency"
                      error={errors.IndependentLivingAgency}
                      value={data.IndependentLivingAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("IndependentLivingAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="IndependentLivingProvider"
                      error={errors.IndependentLivingProvider}
                      value={data.IndependentLivingProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("IndependentLivingProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="IndependentLivingCredentials"
                      error={errors.IndependentLivingCredentials}
                      value={data.IndependentLivingCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("IndependentLivingCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2"></div>
                </div>
                <div className="row border-bottom">
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="OtherServicesProvided"
                      label="Other (e.g. speech, physical therapy, occupational therapy, wrap around services (ABA):"
                      error={errors.OtherServicesProvided}
                      value={data.OtherServicesProvided["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherServicesProvided")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2 border-end">
                    <Input
                      name="OtherServicesProvidedSessions"
                      error={errors.OtherServicesProvidedSessions}
                      value={data.OtherServicesProvidedSessions["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherServicesProvidedSessions")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="OtherServicesProvidedAgency"
                      error={errors.OtherServicesProvidedAgency}
                      value={data.OtherServicesProvidedAgency["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherServicesProvidedAgency")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="OtherServicesProvidedProvider"
                      error={errors.OtherServicesProvidedProvider}
                      value={data.OtherServicesProvidedProvider["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherServicesProvidedProvider")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <Input
                      name="OtherServicesProvidedCredentials"
                      error={errors.OtherServicesProvidedCredentials}
                      value={data.OtherServicesProvidedCredentials["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherServicesProvidedCredentials")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ServicesProvidedNA["@value"] === "true"
                      }
                    />
                  </div>
                  <div className="col-1 py-2"></div>
                </div>
                <div className="row">
                  <div className="col py-2 d-flex">
                    If the child has not received the number or types of
                    services as required by the PCC agreement or specified in
                    the child's treatment plan, indicate the service and reason
                    it was not provided.
                    <Checkbox
                      name="PCCAgreementNA"
                      label="NA"
                      checked={data.PCCAgreementNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="PCCAgreementReason"
                      error={errors.PCCAgreementReason}
                      value={data.PCCAgreementReason["@value"]}
                      onChange={handleChange}
                      identical={compare("PCCAgreementReason")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.PCCAgreementNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="ParticipationLevel"
                      label="Describe child's engagement/participation in treatment."
                      error={errors.ParticipationLevel}
                      value={data.ParticipationLevel["@value"]}
                      onChange={handleChange}
                      identical={compare("ParticipationLevel")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.PCCAgreementNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col pt-2">
                    <div className="d-flex">
                      Have you requested and received approval for a therapeutic
                      exception for this child?
                      <RadioButton
                        name="TherapeuticException"
                        error={errors.TherapeuticException}
                        value={data.TherapeuticException["@value"]}
                        options={yNOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.PCCAgreementNA["@value"] === "true"
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col pb-2">
                    <Input
                      name="TherapeuticExceptionDate"
                      label="If yes, list the date of approval below:"
                      error={errors.TherapeuticExceptionDate}
                      value={data.TherapeuticExceptionDate["@value"]}
                      onChange={handleChange}
                      identical={compare("TherapeuticExceptionDate")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.TherapeuticException["@value"] === "N"
                      }
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <Input
                      name="QMHPExplanation"
                      label={
                        <div className="d-flex">
                          Specialized Programs Only (5S): If the provider is not
                          a QMHP, please explain.
                          <Checkbox
                            name="QMHPNA"
                            label="NA"
                            checked={data.QMHPNA["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                      }
                      error={errors.QMHPExplanation}
                      value={data.QMHPExplanation["@value"]}
                      onChange={handleChange}
                      identical={compare("QMHPExplanation")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.QMHPNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
              </div>
              <br />
              <h5>N. EDUCATION</h5>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="SchoolAndDistrict"
                      label="SCHOOL NAME AND COUNTY/DISTRICT:"
                      error={errors.SchoolAndDistrict}
                      value={data.SchoolAndDistrict["@value"]}
                      onChange={handleChange}
                      identical={compare("SchoolAndDistrict")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-2 py-2 text-center border-end">
                    Current Grade
                  </div>
                  <div className="col-6 py-2 text-center border-end">
                    School Setting
                  </div>
                  <div className="col py-2 text-center">
                    Special Ed. / Other Services Provided
                    <RadioButton
                      name="SpecialEd"
                      error={errors.SpecialEd}
                      value={data.SpecialEd["@value"]}
                      options={yNOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="mb-0"
                    />
                    <div>If yes, please answer the questions below:</div>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-2 py-2 border-end">
                    <Input
                      name="CurrentGrade"
                      error={errors.CurrentGrade}
                      value={data.CurrentGrade["@value"]}
                      onChange={handleChange}
                      identical={compare("CurrentGrade")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-6 py-2 border-end">
                    <div className="row row-cols-1 row-cols-sm-1 row-cols-md-1 row-cols-lg-2 row-cols-xl-2">
                      <div className="col py-2">
                        <Checkbox
                          name="Preschool"
                          label="Pre-school / Head Start"
                          checked={data.Preschool["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        <Checkbox
                          name="DayTreatment"
                          label="Day Treatment"
                          checked={data.DayTreatment["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        <Checkbox
                          name="PrivateSchool"
                          label="Private School"
                          checked={data.PrivateSchool["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        <Checkbox
                          name="College"
                          label="College"
                          checked={data.College["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        <Checkbox
                          name="AlternativeSchool"
                          label="Alternative School"
                          checked={data.AlternativeSchool["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        <Checkbox
                          name="Vocational"
                          label="Vocational"
                          checked={data.Vocational["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        <Checkbox
                          name="PublicSchool"
                          label="Public School"
                          checked={data.PublicSchool["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        If residential indicate:
                        <br />
                        <Checkbox
                          name="CampusOnsite"
                          label="Campus onsite"
                          checked={data.CampusOnsite["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        <Checkbox
                          name="PublicOffsite"
                          label="Public school offsite"
                          checked={data.PublicOffsite["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        <Checkbox
                          name="GED"
                          label="G.E.D."
                          checked={data.GED["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        <Checkbox
                          name="OtherSchoolSetting"
                          label="Other"
                          checked={data.OtherSchoolSetting["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        {data.OtherSchoolSetting["@value"] === "true" ? (
                          <Input
                            name="OtherSchoolSettingDetails"
                            error={errors.OtherSchoolSettingDetails}
                            value={data.OtherSchoolSettingDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherSchoolSettingDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="col py-2">
                    <div className="row">
                      <div className="col">
                        <Input
                          name="SpecialEdServices"
                          label="Please list services provided:"
                          error={errors.SpecialEdServices}
                          value={data.SpecialEdServices["@value"]}
                          onChange={handleChange}
                          identical={compare("SpecialEdServices")}
                          commentsHandler={handleComments}
                          disabled={
                            formDisabled || data.SpecialEd["@value"] === "N"
                          }
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <Input
                          name="SpecialEdDisability"
                          label="Primary disability listed on IEP:"
                          error={errors.SpecialEdDisability}
                          value={data.SpecialEdDisability["@value"]}
                          onChange={handleChange}
                          identical={compare("SpecialEdDisability")}
                          commentsHandler={handleComments}
                          disabled={
                            formDisabled || data.SpecialEd["@value"] === "N"
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    If the school has completed an evaluation of this child or
                    if a new IEP or 504 plan has been developed during this
                    reporting period, please include a copy of the report or
                    plan.
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    Describe child's functioning in each area below:
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 py-2 border-end">
                    Academic Functioning / Grades
                  </div>
                  <div className="col py-2">
                    <Input
                      name="Grades"
                      error={errors.Grades}
                      value={data.Grades["@value"]}
                      onChange={handleChange}
                      identical={compare("Grades")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 py-2 border-end">School Behavior</div>
                  <div className="col py-2">
                    <Input
                      name="SchoolBehavior"
                      error={errors.SchoolBehavior}
                      value={data.SchoolBehavior["@value"]}
                      onChange={handleChange}
                      identical={compare("SchoolBehavior")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-4 py-2 border-end">Other</div>
                  <div className="col py-2">
                    <Input
                      name="OtherFunctioningDetails"
                      error={errors.OtherFunctioningDetails}
                      value={data.OtherFunctioningDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherFunctioningDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <br />
              <h5>O. DAILY LIVING/SOCIAL SKILLS</h5>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="RelationshipDetails"
                      label="Describe the child's interactions/relationships with others as they relate to healthy boundaries and ability to develop bonds. Please list concerns with boundaries and attachments."
                      error={errors.RelationshipDetails}
                      value={data.RelationshipDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("RelationshipDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="Responsibilities"
                      label="Provide a summary of the child's ability to maintain his/her personal hygiene/appearance and complete chores/tasks independently, as appropriate to age and developmental level."
                      error={errors.Responsibilities}
                      value={data.Responsibilities["@value"]}
                      onChange={handleChange}
                      identical={compare("Responsibilities")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <RadioButton
                      name="ReceiveILServices"
                      label="Is the child receiving Independent Living Services?"
                      error={errors.ReceiveILServices}
                      value={data.ReceiveILServices["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Input
                      name="ILServicesDetails"
                      label="If yes, please describe including areas of concern and strengths."
                      error={errors.ILServicesDetails}
                      value={data.ILServicesDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("ILServicesDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.ReceiveILServices["@value"] === "No"
                      }
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>P. DEVELOPMENTAL CONCERNS</h5>
                <Checkbox
                  name="DevelopmentalConcernsNA"
                  label="NA"
                  checked={data.DevelopmentalConcernsNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border">
                <div className="row">
                  <div className="col py-2">
                    <Input
                      name="DevelopmentalConcerns"
                      label="Describe any developmental concerns, including issues with communication, mobility, feeding, drinking, and toileting issues. The reporter should take into account developmentally appropriate skills for child's chronological age and developmental level."
                      error={errors.DevelopmentalConcerns}
                      value={data.DevelopmentalConcerns["@value"]}
                      onChange={handleChange}
                      identical={compare("DevelopmentalConcerns")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.DevelopmentalConcernsNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>Q. PREGNANT/PARENTING YOUTH</h5>
                <Checkbox
                  name="ParentingNA"
                  label="NA"
                  checked={data.ParentingNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col pt-2">
                    <RadioButton
                      name="Pregnant"
                      label="Is the youth currently pregnant?"
                      error={errors.Pregnant}
                      value={data.Pregnant["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={data.ParentingNA["@value"] === "true"}
                    />
                    {data.Pregnant["@value"] !== "No" ? (
                      <Input
                        name="DueDate"
                        label="If yes, anticipated due date:"
                        error={errors.DueDate}
                        value={data.DueDate["@value"]}
                        onChange={handleChange}
                        identical={compare("DueDate")}
                        commentsHandler={handleComments}
                        disabled={
                          data.ParentingNA["@value"] === "true" ||
                          data.Pregnant["@value"] === "No"
                        }
                      />
                    ) : null}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">Identify the youth's children.</div>
                </div>
                <FieldTable
                  name="ChildrenDetails"
                  commentsHandler={handleComments}
                >
                  <div className="row border-bottom">
                    <div className="col border-end py-2 text-center">
                      Child's Name
                    </div>
                    <div className="col-1 border-end py-2 text-center">Age</div>
                    <div className="col border-end py-2">
                      Does the youth currently live with this child?
                    </div>
                    <div className="col border-end py-2">
                      Does the youth have custody?
                    </div>
                    <div className="col-4 py-2">
                      If the youth is not living with this child, what is the
                      current contact or visitation plan?
                    </div>
                  </div>
                  {data.ChildrenDetails["@value"] &&
                    JSON.parse(data.ChildrenDetails["@value"]).map(
                      (item, index) => (
                        <div key={item._id} className="row border-bottom">
                          <div className="col border-end py-2 d-flex">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <>
                                <i
                                  className="fas fa-save me-2 d-print-none"
                                  onClick={() => {
                                    handleSaveRow(index, "ChildrenDetails");
                                  }}
                                ></i>
                                <i
                                  className="far fa-trash-alt me-2 d-print-none"
                                  onClick={() => {
                                    handleDeleteRow(index, "ChildrenDetails");
                                  }}
                                ></i>
                                <Input
                                  name="Name"
                                  value={item.Name}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "ChildrenDetails"
                                    );
                                  }}
                                  disabled={
                                    data.ParentingNA["@value"] === "true"
                                  }
                                />
                              </>
                            ) : (
                              <>
                                {!formDisabled &&
                                data.ParentingNA["@value"] !== "true" ? (
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "ChildrenDetails");
                                    }}
                                  ></i>
                                ) : null}
                                {item.Name}
                              </>
                            )}
                          </div>
                          <div className="col-1 border-end py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="Age"
                                value={item.Age}
                                onChange={(e) => {
                                  handleRowChange(e, index, "ChildrenDetails");
                                }}
                                disabled={data.ParentingNA["@value"] === "true"}
                              />
                            ) : (
                              item.Age
                            )}
                          </div>
                          <div className="col border-end py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <RadioButton
                                name={`LivesWith-${index}`}
                                value={item.LivesWith}
                                options={yesNoOptions}
                                onChange={(e) => {
                                  e.currentTarget = {
                                    ...e.currentTarget,
                                    type: "checkbox",
                                    name: e.currentTarget.name.split("-")[0],
                                  };
                                  handleRowChange(e, index, "ChildrenDetails");
                                }}
                                disabled={data.ParentingNA["@value"] === "true"}
                              />
                            ) : (
                              item.LivesWith
                            )}
                          </div>
                          <div className="col border-end py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <RadioButton
                                name={`HasCustody-${index}`}
                                value={item.HasCustody}
                                options={yesNoOptions}
                                onChange={(e) => {
                                  e.currentTarget = {
                                    ...e.currentTarget,
                                    type: "checkbox",
                                    name: e.currentTarget.name.split("-")[0],
                                  };
                                  handleRowChange(e, index, "ChildrenDetails");
                                }}
                                disabled={data.ParentingNA["@value"] === "true"}
                              />
                            ) : (
                              item.HasCustody
                            )}
                          </div>
                          <div className="col-4 py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <>
                                {item.LivesWith ? (
                                  <Input
                                    name="VisitationPlan"
                                    value={item.VisitationPlan}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "ChildrenDetails"
                                      );
                                    }}
                                    disabled={
                                      data.ParentingNA["@value"] === "true"
                                    }
                                  />
                                ) : null}
                              </>
                            ) : (
                              item.VisitationPlan
                            )}
                          </div>
                        </div>
                      )
                    )}
                  {!formDisabled && data.ParentingNA["@value"] !== "true" ? (
                    <div className="row border-bottom">
                      <div className="col py-2">
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            handleAddRow("ChildrenDetails", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Child
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {errors.ChildrenDetails && (
                    <div className="alert alert-danger">
                      {errors.ChildrenDetails}
                    </div>
                  )}
                </FieldTable>
                {data.Pregnant["@value"] !== "No" ? (
                  <div className="row border-bottom">
                    <div className="col py-2">
                      <Input
                        name="PregnancyComplications"
                        label="Please include any current or potential pregnancy complications and services being provided to address the youth's prenatal care:"
                        error={errors.PregnancyComplications}
                        value={data.PregnancyComplications["@value"]}
                        onChange={handleChange}
                        identical={compare("PregnancyComplications")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                ) : null}
                <div className="row">
                  <div className="col py-2">
                    <Input
                      name="ParentingSkills"
                      label="Describe this youth's current parenting responsibilities and skills, including strengths and potential risk factors. List any services related to parenting skills/classes being provided to the youth."
                      error={errors.ParentingSkills}
                      value={data.ParentingSkills["@value"]}
                      onChange={handleChange}
                      identical={compare("ParentingSkills")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>R. LEGAL CONSIDERATIONS</h5>
                <Checkbox
                  name="LegalNA"
                  label="NA"
                  checked={data.LegalNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border">
                <div className="row">
                  <div className="col py-2">
                    <Input
                      name="LegalIssues"
                      label="Describe any ongoing unresolved legal issues for this child. Please indicate any new charges, court and/or CDW involvement during this reporting period, as well as any court orders or dispositions."
                      error={errors.LegalIssues}
                      value={data.LegalIssues["@value"]}
                      onChange={handleChange}
                      identical={compare("LegalIssues")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.LegalNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
              </div>
              <br />
              <h5>S. DCBS PERMANENCY GOAL &amp; CURRENT DISCHARGE PLAN</h5>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    <RadioButton
                      name="PermanencyGoal"
                      label="Select the child's current DCBS permanency goal."
                      error={errors.PermanencyGoal}
                      value={data.PermanencyGoal["@value"]}
                      options={permanencyGoalOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="RightsEndDate"
                      label={
                        <div className="d-flex">
                          If parental rights have been terminated during this
                          reporting period, provide the date of termination.
                          <Checkbox
                            name="RightsNA"
                            label="NA"
                            checked={data.RightsNA["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                      }
                      error={errors.RightsEndDate}
                      value={data.RightsEndDate["@value"]}
                      onChange={handleChange}
                      identical={compare("RightsEndDate")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.RightsNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="PermanencyServices"
                      label="Describe the services and treatment interventions that your agency is providing to support and promote this child's family connections and permanency. If no services are being provided, explain."
                      error={errors.PermanencyServices}
                      value={data.PermanencyServices["@value"]}
                      onChange={handleChange}
                      identical={compare("PermanencyServices")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="DischargePlan"
                      label="Describe the current discharge plan and indicate the reason for any change during this reporting period."
                      error={errors.DischargePlan}
                      value={data.DischargePlan["@value"]}
                      onChange={handleChange}
                      identical={compare("DischargePlan")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <Input
                      name="ProjectedDischargeDate"
                      label="What is the child's projected discharge date?"
                      error={errors.ProjectedDischargeDate}
                      value={data.ProjectedDischargeDate["@value"]}
                      onChange={handleChange}
                      identical={compare("ProjectedDischargeDate")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>
                  T. FAMILY ENGAGEMENT &amp; VISITATION/CONTACT INFORMATION
                </h5>
                <Checkbox
                  name="VisitationNA"
                  label="NA"
                  checked={data.VisitationNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border-start border-end border-top">
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="FamilyEngagement"
                      label="Describe how your program is engaging or how your program has tried to engage with family members (e.g., parents, siblings, aunts/uncles, grandparents or any other individual in a familial like relationship with the child) to support and promote this child's connections. If no family engagement is occurring, please explain why:"
                      error={errors.FamilyEngagement}
                      value={data.FamilyEngagement["@value"]}
                      onChange={handleChange}
                      identical={compare("FamilyEngagement")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.VisitationNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="FamilyProgress"
                      label="Identify the progress and any barriers encountered during family therapy sessions if the permanency goal is return to parent/relative."
                      error={errors.FamilyProgress}
                      value={data.FamilyProgress["@value"]}
                      onChange={handleChange}
                      identical={compare("FamilyProgress")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || data.VisitationNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    Report any visitation, in person or virtual, by persons
                    outside the current placement agency (e.g., DCBS/Wendy's
                    Wonderful Kids worker, relatives, mentors, etc.) including
                    the dates and results of the visits
                  </div>
                </div>
                <FieldTable
                  name="VisitationsDetails"
                  commentsHandler={handleComments}
                >
                  <div className="row border-bottom">
                    <div className="col py-2 text-center border-end">
                      Date/Length of Visit
                    </div>
                    <div className="col-3 py-2 text-center border-end">
                      Name of Person Visiting with Child
                    </div>
                    <div className="col py-2 text-center border-end">
                      Relation to Child
                    </div>
                    <div className="col py-2 text-center border-end">
                      Location of Visit
                    </div>
                    <div className="col py-2 text-center">
                      Result of Visit
                      <br />
                      (Give a brief description)
                    </div>
                  </div>
                  {data.VisitationsDetails["@value"] &&
                    JSON.parse(data.VisitationsDetails["@value"]).map(
                      (item, index) => (
                        <div key={item._id} className="row border-bottom">
                          <div className="col border-end py-2 d-flex">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <>
                                <i
                                  className="fas fa-save me-2 d-print-none"
                                  onClick={() => {
                                    handleSaveRow(index, "VisitationsDetails");
                                  }}
                                ></i>
                                <i
                                  className="far fa-trash-alt me-2 d-print-none"
                                  onClick={() => {
                                    handleDeleteRow(
                                      index,
                                      "VisitationsDetails"
                                    );
                                  }}
                                ></i>
                                <Input
                                  name="Date"
                                  value={item.Date}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "VisitationsDetails"
                                    );
                                  }}
                                  disabled={
                                    formDisabled ||
                                    data.VisitationNA["@value"] === "true"
                                  }
                                />
                              </>
                            ) : (
                              <>
                                {!formDisabled &&
                                data.VisitationNA["@value"] !== "true" ? (
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "VisitationsDetails"
                                      );
                                    }}
                                  ></i>
                                ) : null}
                                {item.Date}
                              </>
                            )}
                          </div>
                          <div className="col-3 border-end py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="Name"
                                value={item.Name}
                                onChange={(e) => {
                                  handleRowChange(
                                    e,
                                    index,
                                    "VisitationsDetails"
                                  );
                                }}
                                disabled={
                                  formDisabled ||
                                  data.VisitationNA["@value"] === "true"
                                }
                              />
                            ) : (
                              item.Name
                            )}
                          </div>
                          <div className="col border-end py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="Relationship"
                                value={item.Relationship}
                                onChange={(e) => {
                                  handleRowChange(
                                    e,
                                    index,
                                    "VisitationsDetails"
                                  );
                                }}
                                disabled={
                                  formDisabled ||
                                  data.VisitationNA["@value"] === "true"
                                }
                              />
                            ) : (
                              item.Relationship
                            )}
                          </div>
                          <div className="col border-end py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="Location"
                                value={item.Location}
                                onChange={(e) => {
                                  handleRowChange(
                                    e,
                                    index,
                                    "VisitationsDetails"
                                  );
                                }}
                                disabled={
                                  formDisabled ||
                                  data.VisitationNA["@value"] === "true"
                                }
                              />
                            ) : (
                              item.Location
                            )}
                          </div>
                          <div className="col py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="Result"
                                value={item.Result}
                                onChange={(e) => {
                                  handleRowChange(
                                    e,
                                    index,
                                    "VisitationsDetails"
                                  );
                                }}
                                disabled={
                                  formDisabled ||
                                  data.VisitationNA["@value"] === "true"
                                }
                              />
                            ) : (
                              item.Result
                            )}
                          </div>
                        </div>
                      )
                    )}
                  {errors.VisitationsDetails && (
                    <div className="alert alert-danger">
                      {errors.VisitationsDetails}
                    </div>
                  )}
                </FieldTable>
              </div>
              {!formDisabled && data.VisitationNA["@value"] !== "true" ? (
                <button
                  className="btn btn-primary ms-2 mt-2"
                  onClick={() => {
                    handleAddRow("VisitationsDetails", { IsEditable: true });
                  }}
                >
                  Add Visit
                </button>
              ) : null}
              <br />
              <br />
              <div className="d-flex">
                <h5>U. INDEPENDENT LIVING</h5>
                <Checkbox
                  name="IndependentLivingNA"
                  label="NA"
                  checked={data.IndependentLivingNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="SkillsLevel"
                      label={
                        <div className="d-flex">
                          Describe child's level of independent living skills
                          and how the program is providing independent living
                          support/skill training for the child.
                          <Checkbox
                            name="SkillsLevelNA"
                            label="NA"
                            checked={data.SkillsLevelNA["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={
                              formDisabled ||
                              data.IndependentLivingNA["@value"] === "true"
                            }
                            divClassName="ms-3"
                          />
                        </div>
                      }
                      error={errors.SkillsLevel}
                      value={data.SkillsLevel["@value"]}
                      onChange={handleChange}
                      identical={compare("SkillsLevel")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.IndependentLivingNA["@value"] === "true" ||
                        data.SkillsLevelNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <Input
                      name="EmploymentDetails"
                      label={
                        <div className="d-flex">
                          <span>
                            Identify youth's current employment and any
                            employment the youth may have had during this
                            reporting period. Include dates of hire and
                            employment, position/duties, and performance level.
                          </span>
                          <Checkbox
                            name="EmploymentNA"
                            label="NA"
                            checked={data.EmploymentNA["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={
                              formDisabled ||
                              data.IndependentLivingNA["@value"] === "true"
                            }
                            divClassName="ms-3"
                          />
                        </div>
                      }
                      error={errors.EmploymentDetails}
                      value={data.EmploymentDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("EmploymentDetails")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.IndependentLivingNA["@value"] === "true" ||
                        data.EmploymentNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <Input
                      name="LivingArrangement"
                      label={
                        <div className="d-flex">
                          For youth in independent living programs, describe
                          this youth's current living arrangement and identify
                          any issues or concerns.
                          <Checkbox
                            name="LivingArrangementNA"
                            label="NA"
                            checked={
                              data.LivingArrangementNA["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={
                              formDisabled ||
                              data.IndependentLivingNA["@value"] === "true"
                            }
                            divClassName="ms-3"
                          />
                        </div>
                      }
                      error={errors.LivingArrangement}
                      value={data.LivingArrangement["@value"]}
                      onChange={handleChange}
                      identical={compare("LivingArrangement")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.IndependentLivingNA["@value"] === "true" ||
                        data.LivingArrangementNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <h5>V. ADDITIONAL CONSIDERATIONS</h5>
                <Checkbox
                  name="AdditionalConcernsNA"
                  label="NA"
                  checked={data.AdditionalConcernsNA["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
              </div>
              <div className="container border">
                <div className="row">
                  <div className="col py-2">
                    <Input
                      name="AdditionalConcerns"
                      label="Provide any additional information and recommendations for services."
                      error={errors.AdditionalConcerns}
                      value={data.AdditionalConcerns["@value"]}
                      onChange={handleChange}
                      identical={compare("AdditionalConcerns")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        data.AdditionalConcernsNA["@value"] === "true"
                      }
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default KYALP;
